import { Flex, Card, Heading, Text, Button, Badge, Link, Textarea } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import { ConfirmModal, countries, createDataCollectionContext, dbTimeToUserTime, toaster, useDrawer, type ConfirmModalHandle, type DataCollectionContextProps } from '@shared';
import { useMemo, useRef, type JSX } from 'react';
import accountsService from '../../api/services/accountsService';
import { createColumnHelper } from '@tanstack/react-table';
import { useUpdateAccount } from '../../api/hooks/useAccountSuperadmin';
import { useQueryClient } from '@tanstack/react-query';
import Statistics from './Statistics';
import { LuExternalLink } from 'react-icons/lu';
import AccountDrawer from '../accounts/SuperadminAccountsDrawer';
import { RefreshButton } from '../../../../shared/src/dataCollection/RefreshButton';

const columnHelper = createColumnHelper<Account>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function Overview() {
	const { t } = useTranslation();
	const { user } = useSuperadminAuth();
	const { DataCollectionContextProvider, DataCollectionTable, useDataCollection, DataCollectionTableHeader } = useMemo(() => createDataCollectionContext<Account>(), []);
	const { mutate: updateAccount } = useUpdateAccount();
	const queryClient = useQueryClient();
	const { openDrawer } = useDrawer();

	const confirmRef = useRef<ConfirmModalHandle>(null);

	async function handleRejectAccount(accountID: number): Promise<void> {
		let declineReason = '';

		while (true) {
			const response = await confirmRef.current?.open({
				title: t('general.reject_account'),
				content: (
					<Flex flexDir={'column'} gap={2}>
						<Text>{t('general.really_reject_account')}</Text>
						<Textarea maxLength={500} required onChange={e => (declineReason = e.target.value)} />
						{declineReason.trim() === '' && (
							<Text color="red.500" fontSize="sm">
								{t('general.required')}
							</Text>
						)}
					</Flex>
				),
				confirmButtonText: t('general.reject'),
				confirmButtonVariant: 'subtle',
			});

			// user cancelled
			if (response === false) {
				return;
			}

			if (declineReason.trim() !== '') {
				break;
			}
		}

		const payload = {
			accountID,
			accountStatus: 'rejected' as AccountStatus,
			declineReason,
		};

		updateAccount(payload, {
			onSuccess: () => {
				toaster.create({
					type: 'success',
					title: t('general.saved'),
				});
				queryClient.invalidateQueries({ queryKey: ['accounts'] });
				queryClient.invalidateQueries({ queryKey: ['account'] });
				queryClient.invalidateQueries({ queryKey: ['overview-accounts'] });
			},
		});
	}

	async function handleApproveAccount(accountID: number, isDuplicateBusinessID: boolean): Promise<void> {
		const response = await confirmRef.current?.open({
			title: t('general.approve_account'),
			content: <Text>{isDuplicateBusinessID ? t('general.duplicate_account_verify') : t('general.really_approve_account')}</Text>,
			confirmButtonText: t('general.approve'),
			confirmButtonVariant: 'solid',
		});

		if (response === false) {
			return;
		}

		const payload = {
			accountID: accountID,
			accountStatus: 'approved' as AccountStatus,
		};

		updateAccount(payload, {
			onSuccess: () => {
				toaster.create({
					type: 'success',
					title: t('general.saved'),
				});
				queryClient.invalidateQueries({ queryKey: ['accounts'] });
				queryClient.invalidateQueries({ queryKey: ['account'] });
				queryClient.invalidateQueries({ queryKey: ['overview-accounts'] });
			},
		});
	}

	function getAccountStatusTranslation(status: AccountStatus): string {
		switch (status) {
			case 'waitingForApproval':
				return t('general.waiting_for_approval');
			case 'rejected':
				return t('general.rejected');
			case 'approved':
				return t('general.approved');
			case 'locked':
				return t('general.locked');
			default:
				return t('general.unknown');
		}
	}

	function getBadgeColorByStatus(status: AccountStatus): string {
		switch (status) {
			case 'waitingForApproval':
				return 'yellow';
			case 'approved':
				return 'green';
			case 'locked':
				return 'red';
			case 'rejected':
				return 'red';
			default:
				return 'yellow';
		}
	}

	const columns = [
		columnHelper.accessor('accountID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return (
					<Text
						_hover={{ textDecor: 'underline' }}
						cursor={'pointer'}
						onClick={() =>
							openDrawer({
								id: 'accountDrawer',
								content: <AccountDrawer accountID={row.original.accountID} />,
							})
						}
					>
						{row.original.name}
					</Text>
				);
			},
		}),
		columnHelper.accessor('businessID', {
			header: t('general.business_id'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				const cleanBusinessID = row.original.businessID.replace(/-/g, '');
				return (
					<Link variant="underline" target="_blank" href={`https://kauppalehti.fi/yritykset/yritys/${cleanBusinessID}`}>
						{row.original.businessID}
						<LuExternalLink />
					</Link>
				);
			},
		}),
		columnHelper.accessor('accountStatus', {
			header: t('general.status'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.isDuplicateBusinessID) {
					return (
						<Badge size="md" colorPalette={'red'}>
							{t('general.duplicate_account')}
						</Badge>
					);
				}
				return (
					<Badge size="md" colorPalette={getBadgeColorByStatus(row.original.accountStatus)}>
						{getAccountStatusTranslation(row.original.accountStatus)}
					</Badge>
				);
			},
		}),

		columnHelper.display({
			header: t('general.users_name'),
			id: 'userFullName',
			cell: ({ row }) => {
				const firstName = row.original.users?.[0]?.firstName;
				const lastName = row.original.users?.[0]?.lastName;

				return (
					<Text>
						{firstName} {lastName}
					</Text>
				);
			},
		}),
		columnHelper.display({
			header: t('general.users_phone'),
			id: 'userPhone',
			cell: ({ row }) => {
				const phone = row.original.users?.[0]?.phone;
				return (
					<Link href={`tel:${phone}`} userSelect="text">
						{phone}
					</Link>
				);
			},
		}),
		columnHelper.display({
			header: t('general.users_email'),
			id: 'userEmail',
			cell: ({ row }) => {
				const email = row.original.users?.[0]?.email;
				return <Text userSelect={'text'}>{email}</Text>;
			},
		}),

		columnHelper.accessor('vatCode', {
			header: t('general.vat_code'),
			sortingFn: 'basic',
		}),

		columnHelper.accessor('postalAddress', {
			header: t('general.postal_address'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('postalCode', {
			header: t('general.postal_code'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('city', {
			header: t('general.city'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('countryCode', {
			header: t('general.country'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				const country = countries.find(c => c.code === row.original.countryCode.toUpperCase())?.name || t('general.unknown');
				return <Text>{country}</Text>;
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.display({
			id: 'button',
			cell: ({ row }) => {
				return (
					<Flex gap={2} justifyContent={'end'}>
						<Button variant="subtle" size="xs" onClick={() => handleRejectAccount(row.original.accountID)}>
							{t('general.dont_approve')}
						</Button>
						<Button size="xs" onClick={() => handleApproveAccount(row.original.accountID, row.original.isDuplicateBusinessID || false)}>
							{t('general.approve')}
						</Button>
					</Flex>
				);
			},
		}),
	];

	return (
		<Flex flexDirection="column" gap={5} width="100%" alignItems="center">
			<Card.Root maxWidth={'2000px'} width="100%" flex={1} minWidth={'340px'}>
				<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'column'} padding={5} gap={5}>
					<Flex display={'flex'} flexDirection={'column'} justifyContent={'center'}>
						<Heading>
							{t('general.logged_in_as')} {user?.firstName} {user?.lastName}
						</Heading>
					</Flex>

					<DataCollectionContextProvider
						customQueryKey={`overview-accounts`}
						dataKey="accounts"
						keyAttribute="accountID"
						emptyDataText={t('general.no_accounts_found')}
						fetchFn={(...args) => accountsService.getAllResources(...args)}
						queryParams={{
							filters: 'accountStatus:eq:waitingForApproval',
							getRelated: 'users',
							scopeWith: 'timeLatestLogin|creditCount|isDuplicateBusinessID|timeLatestMessageSent',
						}}
						initialItemsPerPage={25}
						columns={columns}
						defaultHiddenColumns={['vatCode', 'postalAddress', 'postalCode', 'city']}
						isServerSide={true}
						isSelectable={false}
						defaultSort={{ id: 'timeCreated', desc: true }}
					>
						<WaitingForApprovalTable
							useDataCollection={useDataCollection}
							DataCollectionTableHeader={DataCollectionTableHeader}
							DataCollectionTable={DataCollectionTable}
						/>
						<ConfirmModal ref={confirmRef} />
					</DataCollectionContextProvider>
				</Card.Body>
			</Card.Root>
			<Statistics />
		</Flex>
	);
}

function WaitingForApprovalTable({
	DataCollectionTable,
	DataCollectionTableHeader,
	useDataCollection,
}: {
	DataCollectionTable: () => JSX.Element;
	DataCollectionTableHeader: ({ isSearchShown }: { isSearchShown: boolean }) => JSX.Element;
	useDataCollection: () => DataCollectionContextProps<Account>;
}) {
	const { t } = useTranslation();
	const { table } = useDataCollection();
	const rows = table.getRowModel().rows;

	if (rows.length === 0) {
		return (
			<Flex gap="4" justifyContent="space-between" width="100%" alignItems="flex-start">
				<Text>{t('general.no_new_registered_accounts_waiting_for_approval')}</Text>
				<Flex gap="4" justify="flex-end" alignItems="flex-start">
					<RefreshButton useDataCollection={useDataCollection} />
				</Flex>
			</Flex>
		);
	}

	return (
		<>
			<Flex flex={1} justifyContent={'space-between'} width="100%" alignItems={'center'}>
				<Flex marginBottom={1} display={'flex'} flexDirection={'column'} justifyContent={'center'}>
					<Heading size="md">{t('general.accounts_waiting_for_approval')}</Heading>
				</Flex>
				<Flex ml="auto">
					<DataCollectionTableHeader isSearchShown={false} />
				</Flex>
			</Flex>

			<DataCollectionTable />
		</>
	);
}
